import {
    ClickElement,
    ClickText,
    ClickType,
    GA4EventName,
    GA4FeatureCategory,
    GA4ItemListName,
    type Product as TrackingProduct,
    trackAnalyticsEvent,
    trackEecAddToCart,
    trackEecProductDetail,
} from '@jsmdg/tracking';
import { ProductPromotionType, ProductPromotionTypeTrackingLabel } from '../../../shared/enums';
import { isValueVoucher } from '../../../shared/product-safeguards';
import { type Location, type Product, type ProductSearchItem } from '../../../shared/types';
import { getPurchaseVariantForTracking } from '../../helper/getPurchaseType';
import { PurchaseType } from '../../types/enums/purchaseType';

async function trackAddToCartClicked(
    clickType: ClickType = ClickType.DefaultUpper,
    clickText: ClickText | string = ClickText.AddToCart,
): Promise<void> {
    await window.yieldToMainThread();
    trackAnalyticsEvent({
        category: 'ProductDetail',
        action: 'ButtonClick',
        label: 'AddToCart',
        eventData: {
            eventName: GA4EventName.ClickEcomCta,
            feature_category: GA4FeatureCategory.ProductdetailBuybox,
            click_element: ClickElement.AddToCart,
            click_text: clickText,
            click_type: clickType,
        },
    });
}

async function trackRatingClicked(): Promise<void> {
    await window.yieldToMainThread();
    trackAnalyticsEvent({
        category: 'ProductDetail',
        action: 'TextClick',
        label: 'Ratings',
        eventData: {
            eventName: GA4EventName.ClickRating,
            feature_category: GA4FeatureCategory.ProductdetailBuybox,
        },
    });
}

function getNudge(product: Product) {
    return product.productNudge
        ? {
              id: product.productNudge?.id,
              text: product.productNudge?.text,
          }
        : undefined;
}

function getParticipants(product: Product) {
    if (isValueVoucher(product)) {
        return {};
    }

    return {
        participants: {
            min: product.minParticipants,
            max: product.maxParticipants,
        },
    };
}

function getPromotion(product: Product) {
    return product.promotionType && product.promotionType !== ProductPromotionType.NONE
        ? ProductPromotionTypeTrackingLabel[product.promotionType]
        : undefined;
}

function trackEECAddToCartAdded(
    product: Product,
    locations: Location[],
    quantity: number,
    hasFriendsDiscount: boolean,
    purchaseType: PurchaseType | undefined,
): void {
    trackEecAddToCart({
        product: {
            id: product.productId,
            url: product.url,
            trackingName: product.trackingName,
            rating: product.rating,
            manufacturerSku: product.manufacturerSku,
            trackingPath: product.trackingPath,
            price: product.price,
            originalPrice: product.originalPrice,
            title: product.title,
            discountBadgeId: product.discountBadge?.id,
            discountBadgeName: product.discountBadge?.text,
            promotion: getPromotion(product),
            locationCount: locations.length,
            ...getParticipants(product),
            hasFriendsDiscount,
            nudge: getNudge(product),
        },
        quantity,
        variant: getPurchaseVariantForTracking(product.type, purchaseType),
    });
}

function trackEECAddToCartAddedCluster(
    product: ProductSearchItem,
    isProductInfoModalOpen?: boolean,
): void {
    trackEecAddToCart({
        product: {
            id: product.productId,
            trackingPath: product.trackingPath,
            rating: product.rating,
            manufacturerSku: product.manufacturerSku,
            price: {
                gross: {
                    amount: product.price.grossAmount,
                    currencyCode: product.price.currencyCode,
                },
            },
            ...(product.originalPrice?.grossAmount && {
                originalPrice: {
                    gross: {
                        amount: product.originalPrice?.grossAmount,
                        currencyCode: product.originalPrice?.currencyCode,
                    },
                },
            }),
            title: product.title,
            locationCount: product.locations?.length,
            trackingName: '',
        },
        quantity: 1,
        variant: getPurchaseVariantForTracking(product.productType, PurchaseType.Voucher),
        itemListName: isProductInfoModalOpen
            ? GA4ItemListName.ClusterPDPPopUp
            : GA4ItemListName.ClusterPDP,
    });
}

function trackEECProductDetailViewed(
    product: Product,
    locations: Location[],
    purchaseType: PurchaseType | undefined,
    minParticipants: number | undefined,
    maxParticipants: number | undefined,
): void {
    trackEecProductDetail(
        {
            manufacturerSku: product.manufacturerSku,
            trackingPath: product.trackingPath,
            price: product.price,
            originalPrice: product.originalPrice,
            title: product.title,
            discountBadgeId: product.discountBadge?.id,
            discountBadgeName: product.discountBadge?.text,
            promotion: getPromotion(product),
            rating: product.rating,
            locationCount: locations.length,
            participants: {
                min: minParticipants,
                max: maxParticipants,
            },
            nudge: getNudge(product),
        } as TrackingProduct,
        getPurchaseVariantForTracking(product.type, purchaseType),
    );
}

async function trackScrollToClusterClicked(
    clickType: ClickType = ClickType.DefaultUpper,
): Promise<void> {
    await window.yieldToMainThread();
    trackAnalyticsEvent({
        category: 'ProductDetail',
        action: 'ButtonClick',
        label: 'AddToCart',
        eventData: {
            eventName: GA4EventName.ClickButton,
            feature_category: GA4FeatureCategory.ProductdetailBuybox,
            click_element: ClickElement.ClusterScrollPDP,
            click_text: ClickText.ClusterScrollPDP,
            click_type: clickType,
        },
    });
}

export {
    trackAddToCartClicked,
    trackEECAddToCartAdded,
    trackEECAddToCartAddedCluster,
    trackEECProductDetailViewed,
    trackRatingClicked,
    trackScrollToClusterClicked,
};
